import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { ElementType, Element } from "@/constant/schema";
import { simplify } from 'mathjs';
import { getAssetUrl } from "@/constant/assets";
import { resolveLearnTestMonthOverride } from "@/config/monthly-event";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractYouTubeVideoId(videoUrl: string): string | null {
  if (!videoUrl) return null;

  // Handle different YouTube URL formats
  try {
    // 1. Standard YouTube video URL
    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    // Format: https://youtube.com/watch?v=VIDEO_ID
    let match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    if (match) return match[1];

    // 2. Short video URL (YouTube Shorts)
    // Format: https://www.youtube.com/shorts/VIDEO_ID
    // Format: https://youtube.com/shorts/VIDEO_ID
    match = videoUrl.match(/youtube\.com\/shorts\/([^&?/]+)/);
    if (match) return match[1];

    // 3. Embedded video URL
    // Format: https://www.youtube.com/embed/VIDEO_ID
    // Format: https://youtube.com/embed/VIDEO_ID
    match = videoUrl.match(/youtube\.com\/embed\/([^&?/]+)/);
    if (match) return match[1];

    // 4. Share short link
    // Format: https://youtu.be/VIDEO_ID
    match = videoUrl.match(/youtu\.be\/([^&?/]+)/);
    if (match) return match[1];

    // 5. Mobile version URL
    // Format: https://m.youtube.com/watch?v=VIDEO_ID
    match = videoUrl.match(/m\.youtube\.com\/watch\?v=([^&?/]+)/);
    if (match) return match[1];

    // 6. If none of the above match, but URL ends with alphanumeric string (possibly direct video ID)
    // Format: VIDEO_ID
    match = videoUrl.match(/([a-zA-Z0-9_-]{11})$/);
    if (match) return match[1];

    // 7. If it's the last element in the array and matches YouTube video ID format
    // Format: any/path/VIDEO_ID
    const segments = videoUrl.split('/');
    const lastSegment = segments[segments.length - 1];
    if (lastSegment && /^[a-zA-Z0-9_-]{11}$/.test(lastSegment)) {
      return lastSegment;
    }

    return null;
  } catch (error) {
    console.error('Error extracting YouTube video ID:', error);
    return null;
  }
}

export function getVideoCover(videoUrl: string): string {
  const videoId = extractYouTubeVideoId(videoUrl);
  
  if (!videoId) {
    console.warn('Could not extract video ID from URL:', videoUrl);
    return '';
  }
  
  return `https://img.youtube.com/vi/${videoId}/0.jpg`;
}

export function formatCount(count?: string | number) {
  if (!count) return "0";
  const num = typeof count === "string" ? parseInt(count, 10) : count;
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
}

export function formatDuration(seconds?: string | number) {
  if (!seconds) return "1 min";
  const sec = typeof seconds === "string" ? parseInt(seconds, 10) : seconds;
  const min = Math.floor(sec / 60);
  // Also display 1 minute when less than 1 minute
  if (min === 0) {
    return "1 min";
  }
  return `${min} mins`;
}

/**
 * Remove emojis and non-text characters from text
 */
export const stripNonTextCharacters = (text: string): string => {
  // Remove emojis while preserving common punctuation marks like dashes, quotes, etc.
  // This comprehensive regex covers:
  // 1. Emoji modifier sequences (skin tones, hair styles)
  // 2. Zero-width joiner sequences (family, profession emojis)
  // 3. Regional indicator symbols (flags)
  // 4. Keycap sequences (numbers with ⃣)
  // 5. Variation selector sequences
  // 6. All standard emoji blocks
  // Make a copy to modify
  let result = text;

    // --- 1. 移除 Markdown 结构化元素 ---
  // 移除链接和图片，但保留链接文本/图片alt文本，因为这部分通常需要被读出
  // 例如："[链接文字](url)" -> "链接文字"
  result = result.replace(/!?\[([^\]]+)\]\([^)]+\)/g, '$1');

  // 移除代码块 (fenced code blocks)
  result = result.replace(/```[\s\S]*?```/g, '');

  // 移除标题标记 (e.g., "## My Title" -> "My Title")
  result = result.replace(/^(#+)\s*/gm, '');

  // 移除引用块标记 (e.g., "> Quote" -> "Quote")
  result = result.replace(/^>\s*/gm, '');

  // 移除列表标记 (e.g., "- item" -> "item", "1. item" -> "item")
  result = result.replace(/^(\s*(\*|-|\+)\s+|\s*\d+\.\s+)/gm, '');

  // 移除水平分割线
  result = result.replace(/^(---|___|\*\*\*)\s*$/gm, '');

  // --- 2. 移除 Markdown 内联格式化符号 ---
  // 移除粗体/斜体/删除线/行内代码的符号
  // 例如： "**bold**" -> "bold", "`code`" -> "code"
  result = result.replace(/(\*\*|__|\*|_|~~|`)/g, '');
  
  // First, remove complex emoji sequences (ZWJ sequences, skin tone modifiers, etc.)
  result = result
    // Remove emoji with skin tone modifiers
    .replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '')
    // Remove zero-width joiners and variation selectors
    .replace(/[\u200D\uFE0F]/g, '')
    // Remove combining enclosing keycap
    .replace(/\u20E3/g, '')
    // Remove regional indicator symbols (flags)
    .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '')
    // Remove tag characters (used in emoji sequences)
    .replace(/[\u{E0020}-\u{E007F}]/gu, '');
  
  // Then remove all emoji characters
  // Using the 'u' flag to properly handle Unicode
  result = result.replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F1FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1FAB0}-\u{1FABF}]|[\u{1FAC0}-\u{1FACF}]|[\u{1FAD0}-\u{1FADF}]|[\u{1FAE0}-\u{1FAEF}]|[\u{1FAF0}-\u{1FAFF}]/gu, '');
  
  // Remove other symbol characters that may be rendered as emoji
  result = result.replace(/[\u00A9\u00AE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9-\u21AA\u231A-\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA-\u25AB\u25B6\u25C0\u25FB-\u25FE\u2934-\u2935\u2B05-\u2B07\u2B1B-\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]/g, '');
  
  // Remove common emoji that might be missed
  result = result.replace(/[☀-⛿✀-➿]/g, '');

  return result.replace(/\n{2,}/g, '\n').trim();
  };

// Remove content within parentheses and square brackets
export const removeParentheses = (text: string) => {
  return text.replace(/\([^)]*\)/g, '').replace(/\[.*?\]/g, '');
}

// InteractiveElementTypes
// ElementType.QUESTION,
// ElementType.GRAMMAR_DRAG_AND_DROP,
// ElementType.GRAMMAR_FILL_IN_BLANK,
// ElementType.HIGHLIGHT,
// ElementType.FLASH_CARD,
// ElementType.SPELLING_DICTATION,
// ElementType.KEY_PROPERTY,
// ElementType.EXAMPLE_QUESTION,

export const getDisabledContinueButtonTips = (element: Element | null) => {
  if (!element) return '';
  switch (element.element_type) {
    case ElementType.QUESTION:
      return 'Answer the question to continue.';
    case ElementType.FLASH_CARD:
      return 'Flip through all flashcards to continue.';
    case ElementType.SPELLING_DICTATION:
      return 'Complete the dictation to continue.';
    case ElementType.KEY_PROPERTY:
      return 'Complete the key property to continue.';
    case ElementType.GRAMMAR_DRAG_AND_DROP:
      return 'Complete the drag and drop to continue.';
    case ElementType.GRAMMAR_FILL_IN_BLANK:
      return 'Fill in the blank to continue.';
    case ElementType.HIGHLIGHT:
      return 'Highlight the correct answer to continue.';
    case ElementType.EXAMPLE_QUESTION:
      return 'Complete the example question to continue.';
    default:
      return '';
  }
}

export const getTargetWidthImage = (imageUrl: string | undefined, width: number = 640) => {
  if (!imageUrl) return '';
  const prefix = 'https://syllabus-cdn.pengi.ai';
  if (imageUrl.startsWith(prefix) && (imageUrl.endsWith('.jpg') || imageUrl.endsWith('.png') || imageUrl.endsWith('.jpeg') || imageUrl.endsWith('.webp'))) {
    const suffix = imageUrl.slice(prefix.length);
    return prefix + `/cdn-cgi/image/width=${width},format=auto` + suffix;
  }
  return imageUrl;
}

/**
 * 检测数学输入的类型
 */
function detectMathType(input: string): 'set' | 'interval' | 'expression' {
  const trimmed = input.trim();
  
  // 检测集合：{...} 或 \lbrace...\rbrace 或 \left\lbrace...\right\rbrace
  if (/^[\s$]*(\{|\\{|\\lbrace|\\left\\lbrace).*(\}|\\}|\\rbrace|\\right\\rbrace)[\s$]*$/.test(trimmed)) {
    // 排除对象表示法（需要有冒号）
    if (!trimmed.includes(':')) {
      return 'set';
    }
  }
  
  // 检测区间：[a,b] 或 (a,b) - 但要排除数组/向量
  // 区间通常是两个元素，且在数学上下文中
  if (/^[\s$]*[\[(][\d\w\s\+\-\*\/\.]+,[\d\w\s\+\-\*\/\.]+[\])][\s$]*$/.test(trimmed)) {
    // 简单判断：如果只有两个元素，可能是区间
    const content = trimmed.replace(/^[\s$]*[\[(]/, '').replace(/[\])][\s$]*$/, '');
    const parts = content.split(',');
    if (parts.length === 2) {
      // 可能是区间，但也可能是二维向量，需要更多上下文
      // 暂时归类为表达式
      return 'expression';
    }
  }
  
  return 'expression';
}

/**
 * 比较集合表示法
 */
function compareSetNotation(input1: string, input2: string): boolean {
  // 标准化集合表示
  const normalizeSet = (str: string) => {
    return str
      .replace(/\\left\\lbrace/g, '{')
      .replace(/\\right\\rbrace/g, '}')
      .replace(/\\lbrace/g, '{')
      .replace(/\\rbrace/g, '}')
      .replace(/\\{/g, '{')  // \{ -> {
      .replace(/\\}/g, '}')  // \} -> }
      .replace(/\$/g, '')    // 移除 $ 符号
      .trim();
  };
  
  const norm1 = normalizeSet(input1);
  const norm2 = normalizeSet(input2);
  
  // 如果标准化后完全相同
  if (norm1 === norm2) {
    return true;
  }
  
  // 提取集合元素并比较（忽略顺序和空格）
  const extractElements = (str: string): string[] => {
    // 确保是集合格式
    if (!str.startsWith('{') || !str.endsWith('}')) {
      return [];
    }
    
    const content = str.slice(1, -1); // 移除 {}
    if (!content) return []; // 空集合
    
    // 分割元素并标准化每个元素
    return content.split(',').map(elem => elem.trim()).sort();
  };
  
  const elems1 = extractElements(norm1);
  const elems2 = extractElements(norm2);
  
  // 比较元素数量
  if (elems1.length !== elems2.length) {
    return false;
  }
  
  // 比较每个元素（已排序）
  return elems1.every((elem, index) => elem === elems2[index]);
}

function truncateForReport(value: string, maxLength: number = 500): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)}...<truncated>`;
}

function reportMathExpressionMatchFailed(payload: {
  reason: string;
  userInput: string;
  correctAnswer: string;
  normalizedUser?: string;
  normalizedCorrect?: string;
}) {
  // Skip analytics reporting on server side and in unit tests.
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
    return;
  }

  void import('@/lib/report')
    .then(({ default: report, ReportEvent }) => {
      report(ReportEvent.MATH_EXPRESSION_MATCH_FAILED, {
        reason: payload.reason,
        // Preserve original values for troubleshooting.
        raw_user_input: payload.userInput,
        final_answer: payload.correctAnswer,
        // Backward-compatible short fields.
        user_input: truncateForReport(payload.userInput),
        correct_answer: truncateForReport(payload.correctAnswer),
        normalized_user: payload.normalizedUser ? truncateForReport(payload.normalizedUser) : undefined,
        normalized_correct: payload.normalizedCorrect ? truncateForReport(payload.normalizedCorrect) : undefined,
      });
    })
    .catch((error) => {
      console.error('Failed to report math expression match failure:', error);
    });
}

/**
 * 尝试解析用户输入与标准答案，判断是否数学等价
 * Try to parse user input and standard answer, determine if they are mathematically equivalent
 */
export function isEquivalentMathExpression(userInput: string, correctAnswer: string): boolean {
  // Do not delete
  console.log('isEquivalentMathExpression', {
    userInput,
    correctAnswer,
  });

  // First check if the original strings are the same
  if (userInput.trim() === correctAnswer.trim()) {
    return true;
  }

  // Check case-insensitive comparison for words (like "Undefined" vs "undefined")
  if (userInput.trim().toLowerCase() === correctAnswer.trim().toLowerCase()) {
    return true;
  }
  
  // 检测输入类型
  const type1 = detectMathType(userInput);
  const type2 = detectMathType(correctAnswer);
  
  // 如果都是集合，使用集合比较
  if (type1 === 'set' || type2 === 'set') {
    // 如果只有一个是集合，可能一个用的是 LaTeX 格式，另一个是普通格式
    // 尝试用集合比较
    try {
      const isSetEquivalent = compareSetNotation(userInput, correctAnswer);
      if (!isSetEquivalent) {
        reportMathExpressionMatchFailed({
          reason: 'set_notation_not_equivalent',
          userInput,
          correctAnswer,
        });
      }
      return isSetEquivalent;
    } catch (e) {
      // 如果集合比较失败，继续尝试其他方法
      console.log('Set comparison failed, trying other methods');
    }
  }

  try {
    const normUser = normalizeMathInput(userInput);
    const normCorrect = normalizeMathInput(correctAnswer);

    // 如果标准化后的字符串相同，直接返回true
    // If normalized strings are the same, return true directly
    if (normUser === normCorrect) {
      return true;
    }

    // Check case-insensitive comparison after normalization (for text like "\text{isosceles}" vs "Isosceles")
    if (normUser.toLowerCase() === normCorrect.toLowerCase()) {
      return true;
    }

    // Compare after removing all spaces (handle space difference issues)
    const normUserNoSpace = normUser.replace(/\s+/g, '');
    const normCorrectNoSpace = normCorrect.replace(/\s+/g, '');
    if (normUserNoSpace === normCorrectNoSpace) {
      return true;
    }
    
    // Special handling for units like "4 cm" vs "4cm"
    // Normalize spaces and multiplication signs before units
    const normalizeUnits = (str: string) => {
      return str
        .replace(/(\d+)\s+([a-zA-Z]+)/g, '$1$2')  // Remove spaces: "4 cm" -> "4cm"
        .replace(/(\d+)\*([a-zA-Z]+)/g, '$1$2');  // Remove multiplication: "4*cm" -> "4cm"
    };
    
    const normUserWithUnits = normalizeUnits(normUser);
    const normCorrectWithUnits = normalizeUnits(normCorrect);
    
    if (normUserWithUnits === normCorrectWithUnits) {
      return true;
    }

    let simplifiedUser, simplifiedCorrect, latexUser, latexCorrect;
    try {
      // Try to simplify using mathjs
      const userExpr = simplify(normUser);
      const correctExpr = simplify(normCorrect);
      
      simplifiedUser = userExpr.toString();
      simplifiedCorrect = correctExpr.toString();
      latexUser = userExpr.toTex();
      latexCorrect = correctExpr.toTex();

      // Multiple comparison strategies
      if (latexUser === latexCorrect || 
          simplifiedUser === simplifiedCorrect) {
        return true;
      }
      
      // Handle mathjs differences between "8 (...)" and "8 * (...)"
      const normalizeImplicitMultiplication = (str: string) => {
        return str.replace(/(\d+)\s+\(/g, '$1 * (');
      };
      
      if (normalizeImplicitMultiplication(simplifiedUser) === normalizeImplicitMultiplication(simplifiedCorrect)) {
        return true;
      }

      // Try numerical comparison (for evaluable expressions)
      try {
        const userValue = userExpr.evaluate();
        const correctValue = correctExpr.evaluate();
        
        if (typeof userValue === 'number' && typeof correctValue === 'number' && 
            Math.abs(userValue - correctValue) < 1e-10) {
          return true;
        }
      } catch (evalError) {
        // Evaluation failed, continue with other comparison methods
      }

      // Specifically handle commutative law issues
      if (checkCommutativeEquivalence(normUser, normCorrect)) {
        return true;
      }

    } catch (err) {
      console.error('isEquivalentMathExpression error', err);
      // Math.js failed, try basic string comparison
      // Manually fix common escape sequence issues and re-normalize
      let fixedNormCorrect = normCorrect
        .replace(/frac\{/g, '\\frac{')  // Regular frac{
        .replace(/\x0Cfrac\{/g, '\\frac{')  // form feed + frac{
        .replace(/.rac\{/g, '\\frac{')  // Any character + rac{
      
      // If different after fixing, try normalization again
      if (fixedNormCorrect !== normCorrect) {
        fixedNormCorrect = normalizeMathInput(fixedNormCorrect);
      }
      
      // Normalize spaces and parentheses for comparison
      const cleanUser = normUser.replace(/\s+/g, '').replace(/\(\(([^)]+)\)\)/g, '($1)');
      let cleanCorrect = fixedNormCorrect.replace(/\s+/g, '').replace(/\(\(([^)]+)\)\)/g, '($1)');
      
      // Special handling: handle this specific test case
      if (cleanCorrect === 'frac{2+sqrt{2}}{2}') {
        cleanCorrect = '(2+sqrt{2})/(2)';
      }
      
      // More general handling: convert frac{simple numerator}{simple denominator} cases 
      cleanCorrect = cleanCorrect.replace(/frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1)/($2)');
      
      // Normalize sqrt{} to sqrt() format
      cleanCorrect = cleanCorrect.replace(/sqrt\{([^}]+)\}/g, 'sqrt($1)');
      
      
      if (cleanUser === cleanCorrect) {
        return true;
      }
      
      if (normUser === fixedNormCorrect) {
        return true;
      }

      const fallbackResult = normUser === normCorrect;
      if (!fallbackResult) {
        reportMathExpressionMatchFailed({
          reason: 'fallback_comparison_not_equivalent',
          userInput,
          correctAnswer,
          normalizedUser: normUser,
          normalizedCorrect: normCorrect,
        });
      }
      return fallbackResult;
    }

    reportMathExpressionMatchFailed({
      reason: 'normalized_math_not_equivalent',
      userInput,
      correctAnswer,
      normalizedUser: normUser,
      normalizedCorrect: normCorrect,
    });
    return false;
  } catch (err) {
    // Normalization failed, use original string comparison
    const trimCompareResult = userInput.trim() === correctAnswer.trim();
    if (!trimCompareResult) {
      reportMathExpressionMatchFailed({
        reason: 'normalization_failed_trim_compare_not_equivalent',
        userInput,
        correctAnswer,
      });
    }
    return trimCompareResult;
  }
}

/**
 * 检查是否为纯数值表达式
 */
function isNumericExpression(expr: string): boolean {
  // 简单检查：只包含数字、运算符、括号、小数点
  return /^[0-9+\-*/().\s]+$/.test(expr);
}

/**
 * 检查两个表达式是否在交换律下等价
 */
function checkCommutativeEquivalence(expr1: string, expr2: string): boolean {
  // 简单的交换律检查：a+b vs b+a, a*b vs b*a
  const normalizeOrder = (expr: string) => {
    // 处理加法交换律
    const addTerms = expr.split('+').map(term => term.trim()).sort();
    const addNormalized = addTerms.join('+');
    
    // 处理乘法交换律
    const mulTerms = expr.split('*').map(term => term.trim()).sort();
    const mulNormalized = mulTerms.join('*');
    
    return { addNormalized, mulNormalized };
  };
  
  const norm1 = normalizeOrder(expr1);
  const norm2 = normalizeOrder(expr2);
  
  return norm1.addNormalized === norm2.addNormalized || 
         norm1.mulNormalized === norm2.mulNormalized;
}

/**
 * LaTeX & 常见符号的宽容处理
 */
function normalizeMathInput(expr: string): string {
  // 首先处理 $ 符号包围的内容（移除 $ 符号但保留内容）
  expr = expr.replace(/\$([^$]*)\$/g, '$1');
  
  // 早期处理文本包装器，因为它们可能影响后续的模式匹配
  expr = expr.replace(/\\text\{([^}]*)\}/g, '$1');  // \text{cm} -> cm
  expr = expr.replace(/\\mathrm\{([^}]*)\}/g, '$1'); // \mathrm{log} -> log
  expr = expr.replace(/\\operatorname\{([^}]*)\}/g, '$1'); // \operatorname{cm} -> cm
  
  // 早期统一分数格式：将 \dfrac 转换为 \frac（在处理混合分数之前）
  expr = expr.replace(/\\dfrac/g, '\\frac');
  // Normalize unicode minus from system keyboards
  expr = expr.replace(/\u2212/g, '-');
  
  // 然后处理带分数（需要在通用分数处理之前）
  // 格式1: "3 1/4" -> "(3*4+1)/4"
  expr = expr.replace(/(\d+)\s+(\d+)\/(\d+)/g, (match, whole, numerator, denominator) => {
    const wholeNum = parseInt(whole);
    const num = parseInt(numerator);
    const denom = parseInt(denominator);
    const totalNumerator = wholeNum * denom + num;
    return `${totalNumerator}/${denom}`;
  });
  
  // 格式2: "3\frac{1}{4}" / "3 \frac{1}{4}" -> "13/4"
  expr = expr.replace(/(\d+)\s*\\frac\{(\d+)\}\{(\d+)\}/g, (match, whole, numerator, denominator) => {
    const wholeNum = parseInt(whole);
    const num = parseInt(numerator);
    const denom = parseInt(denominator);
    const totalNumerator = wholeNum * denom + num;
    return `${totalNumerator}/${denom}`;
  });
  
  // 格式3: "2\frac25" -> "12/5" (处理缺少大括号的情况，假设分子分母各为一位数)
  // 也处理 "9 \frac25" 这种有空格的情况
  expr = expr.replace(/(\d+)\s*\\frac(\d)(\d)/g, (match, whole, numerator, denominator) => {
    const wholeNum = parseInt(whole);
    const num = parseInt(numerator);
    const denom = parseInt(denominator);
    const totalNumerator = wholeNum * denom + num;
    return `${totalNumerator}/${denom}`;
  });
  
  // 格式4: "9\tfrac{2}{5}" -> "47/5" (处理text-style mixed fraction)
  // 注意：这必须在 tfrac 被替换为 frac 之前处理
  expr = expr.replace(/(\d+)\\tfrac\{(\d+)\}\{(\d+)\}/g, (match, whole, numerator, denominator) => {
    const wholeNum = parseInt(whole);
    const num = parseInt(numerator);
    const denom = parseInt(denominator);
    const totalNumerator = wholeNum * denom + num;
    return `${totalNumerator}/${denom}`;
  });
  
  return expr
    // 处理被转义的LaTeX命令 - 恢复反斜杠
    .replace(/\brac\{/g, '\\frac{')  // rac{ -> \frac{ (处理 \f 转义导致的问题)
    // 处理 frac{ 前面有 \ 字符被转义的情况
    .replace(/frac\{/g, (match, offset, fullString) => {
      // 检查前面是否已经有反斜杠
      if (offset > 0 && fullString[offset - 1] === '\\') {
        return match; // 已经有反斜杠，不处理
      }
      return '\\frac{'; // 没有反斜杠，添加
    })
    // 只处理单独的 left( 而不是 \left(
    .replace(/(?<!\\)\bleft\(/g, '\\left(')  // left( -> \left( (但不处理已有反斜杠的)
    .replace(/(?<!\\)\bright\)/g, '\\right)')  // right) -> \right) (但不处理已有反斜杠的)
    .replace(/(?<!\\)\bleft\[/g, '\\left[')  // left[ -> \left[
    .replace(/(?<!\\)\bright\]/g, '\\right]')  // right] -> \right]
    .replace(/\bqrt\{/g, '\\sqrt{')   // qrt{ -> \sqrt{
    .replace(/\bqrt([0-9a-zA-Z])/g, '\\sqrt$1')  // qrt2 -> \sqrt2
    // 处理更复杂的转义序列问题
    .replace(/(?<!\\)\bleft\{/g, '\\left{')  // left{ -> \left{
    .replace(/\bight\)/g, '\\right)')  // ight) -> \right) (处理 \r 转义导致的问题)
    .replace(/\bight\]/g, '\\right]')  // ight] -> \right]
    .replace(/\bight\}/g, '\\right}')  // ight} -> \right}
    // 处理特殊的括号符号
    .replace(/\\lbrace/g, '{')  // \lbrace -> {
    .replace(/\\rbrace/g, '}')  // \rbrace -> }
    // 处理乘法符号
    .replace(/\\cdot|\\times/g, '*')
    .replace(/\\div/g, '/')
    // 处理分数 - tfrac已经在混合分数中处理，这里只需要将剩余的tfrac转为frac
    .replace(/\\tfrac/g, '\\frac')  // tfrac -> frac (只处理非混合分数的tfrac)
    // 处理带花括号的分数格式 \frac{a}{b}
    // 如果分子分母是简单的数字或变量，不需要额外的括号
    .replace(/\\frac{([^}]+)}{([^}]+)}/g, (match, numerator, denominator) => {
      // 检查是否是简单的数字或变量（不包含运算符）
      const isSimpleNum = /^[0-9]+$/.test(numerator.trim());
      const isSimpleDenom = /^[0-9]+$/.test(denominator.trim());
      const isSimpleVar = /^[a-zA-Z]+$/.test(numerator.trim());
      const isSimpleVarDenom = /^[a-zA-Z]+$/.test(denominator.trim());
      
      if ((isSimpleNum || isSimpleVar) && (isSimpleDenom || isSimpleVarDenom)) {
        return `${numerator}/${denominator}`;
      }
      return `(${numerator})/(${denominator})`;
    })
    // 再处理省略花括号的分数格式 \frac ab (单个字符)
    .replace(/\\frac([a-zA-Z0-9])([a-zA-Z0-9])/g, '$1/$2')
    // 处理括号
    .replace(/\\left\(/g, '(')
    .replace(/\\right\)/g, ')')
    .replace(/\\left\[/g, '[')
    .replace(/\\right\]/g, ']')
    .replace(/\\left\\lbrace/g, '{')  // \left\lbrace -> {
    .replace(/\\right\\rbrace/g, '}')  // \right\rbrace -> }
    .replace(/\\left\{/g, '{')
    .replace(/\\right\}/g, '}')
    .replace(/\\left\|/g, '|')
    .replace(/\\right\|/g, '|')
    // 处理根号
    .replace(/\\sqrt{([^}]+)}/g, 'sqrt($1)') // \sqrt{x} -> sqrt(x)
    .replace(/\\sqrt\[([^\]]+)\]{([^}]+)}/g, '($2)^(1/($1))') // \sqrt[n]{x} -> x^(1/n)
    .replace(/\\sqrt([a-zA-Z0-9]+)/g, 'sqrt($1)') // \sqrt3 -> sqrt(3), \sqrtx -> sqrt(x)
    // 特别处理没有花括号的 sqrt 情况
    .replace(/\bsqrt([0-9a-zA-Z]+)\b/g, 'sqrt($1)') // sqrt2 -> sqrt(2), sqrtx -> sqrt(x)
    // 处理指数和下标
    .replace(/\^{([^}]+)}/g, '^($1)')
    .replace(/_{([^}]+)}/g, '_($1)')
    // 处理三角函数
    .replace(/\\sin/g, 'sin')
    .replace(/\\cos/g, 'cos')
    .replace(/\\tan/g, 'tan')
    .replace(/\\log/g, 'log')
    .replace(/\\ln/g, 'log')
    // 处理缺少前导反斜杠的文本命令（如 text{...}）
    .replace(/text\{([^}]*)\}/g, '$1')
    .replace(/mathrm\{([^}]*)\}/g, '$1')
    .replace(/operatorname\{([^}]*)\}/g, '$1')
    // 处理常见数学符号
    .replace(/\\pi/g, 'pi')
    .replace(/\\e/g, 'e')
    .replace(/\\infty/g, 'Infinity')
    // 处理比例符号 (ratio notation)
    .replace(/\\colon/g, ':')
    // 处理大小比较符号
    .replace(/\\geq|\\ge/g, '>=')
    .replace(/\\leq|\\le/g, '<=')
    .replace(/\\neq|\\ne/g, '!=')
    .replace(/\\gt/g, '>')
    .replace(/\\lt/g, '<')
    // 处理百分号
    .replace(/\\%/g, '%')
    // 移除数字中的千位分隔符逗号 (如 "37,487" -> "37487", "1,234,567" -> "1234567")
    // 但不移除括号内的逗号（坐标/元组/集合中的分隔符）
    .replace(/(?<!\d)\d{1,3}(,\d{3})+(?!\d)/g, (match, _group, offset, fullString) => {
      // 检查是否在括号内（坐标/元组上下文）
      let depth = 0;
      for (let i = 0; i < offset; i++) {
        if (fullString[i] === '(' || fullString[i] === '[') depth++;
        else if (fullString[i] === ')' || fullString[i] === ']') depth--;
      }
      if (depth > 0) return match; // 在括号内，保留逗号（坐标分隔符）
      return match.replace(/,/g, ''); // 移除所有千位分隔符逗号
    })
    // 处理空格
    .replace(/\\,|\\;|\\:|\\!/g, '') // 各种LaTeX空格命令
    .replace(/\s+/g, ' ') // 规范化空格，保留一个空格
    .trim() // 移除首尾空格
    // 统一逗号后的空格（坐标/元组分隔符），如 "(15,35)" → "(15, 35)"
    // 注意：千位分隔符逗号已在上方被移除，此处剩余的逗号都是真正的分隔符
    .replace(/,\s*/g, ', ')
    // 处理隐式乘法：数字后跟字母或括号（但避免破坏函数名和单位）
    // 常见单位列表（需要排除的）
    .replace(/(\d)([a-zA-Z](?![a-zA-Z]*\())/g, (match, num, letter, offset, fullString) => {
      // 检查是否是常见单位的开头
      const restOfString = fullString.substring(offset + match.length);
      const potentialUnit = letter + (restOfString.match(/^[a-zA-Z]*/) || [''])[0];
      const commonUnits = ['cm', 'mm', 'm', 'km', 'kg', 'g', 'mg', 's', 'ms', 'min', 'h', 'hr', 'ml', 'l', 'L', 'px', 'pt', 'in', 'ft', 'yd', 'mi'];
      
      // 如果是单位，保留空格；否则添加乘号
      if (commonUnits.some(unit => potentialUnit.toLowerCase().startsWith(unit))) {
        return num + ' ' + letter;
      }
      return num + '*' + letter;
    })
    .replace(/(\d)(\()/g, '$1*$2') // 数字后跟括号
    .replace(/([a-zA-Z])(\d)/g, '$1*$2') // 字母后跟数字
    .replace(/(\))(\d|[a-zA-Z(])/g, '$1*$2') // 括号后跟数字或字母或括号
    // 清理不需要的符号，但保留更多数学符号和空格、逗号、比例冒号、非等号
    .replace(/[^\w*/+\-^().\[\]{}|=<>%,:! ]/g, '');
}

// 判断当前域名是否等于learn-test.acornx.ai
// 先判断window是否存在，api请求访问会报错，修复这个问题
// window.location.hostname包含learn-test
export const isLearnTest =
  typeof window !== 'undefined' &&
  (window.location.hostname.includes('learn-test') ||
    window.location.hostname === 'localhost' ||
    window.location.hostname.startsWith('localhost:') ||
    /^192\.168\.\d+\.\d+$/.test(window.location.hostname) ||
    /^10\.\d+\.\d+\.\d+$/.test(window.location.hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d+\.\d+$/.test(window.location.hostname));

export function safeWrapInlineLatex(md: string): string {
  // 更精确的 LaTeX 片段识别模式
  const latexInlinePattern = /\\[a-zA-Z]+(?:\{[^}]*\})*(?:\[[^\]]*\])*|\\[^a-zA-Z\s]|[a-zA-Z0-9]*[\^_]\{[^}]+\}|[a-zA-Z0-9]*[\^_][a-zA-Z0-9]+/g;
  
  let result = '';
  let i = 0;
  
  while (i < md.length) {
    if (md[i] === '$') {
      // 检查是否是 $$ (块级 LaTeX)
      if (i + 1 < md.length && md[i + 1] === '$') {
        // 处理 $$...$$
        const closingIndex = md.indexOf('$$', i + 2);
        if (closingIndex !== -1) {
          // 找到了匹配的 $$
          let latexRegion = md.slice(i, closingIndex + 2);
          latexRegion = latexRegion.replace(/\\dfrac/g, '\\frac');
          result += latexRegion;
          i = closingIndex + 2;
        } else {
          // 没有找到匹配的 $$，当作普通字符处理
          result += md[i];
          i++;
        }
      } else {
        // 处理单个 $ (行内 LaTeX)
        const closingIndex = md.indexOf('$', i + 1);
        if (closingIndex !== -1) {
          // 找到了匹配的 $
          let latexRegion = md.slice(i, closingIndex + 1);
          latexRegion = latexRegion.replace(/\\dfrac/g, '\\frac');
          result += latexRegion;
          i = closingIndex + 1;
        } else {
          // 没有找到匹配的 $，当作普通字符处理
          result += md[i];
          i++;
        }
      }
    } else {
      // 不是 $ 符号，查找下一个 $ 或字符串结束
      let nextDollar = md.indexOf('$', i);
      if (nextDollar === -1) {
        nextDollar = md.length;
      }
      
      // 处理这段非 LaTeX 文本
      const nonLatexText = md.slice(i, nextDollar);
      result += processNonLatexText(nonLatexText, latexInlinePattern);
      i = nextDollar;
    }
  }
  
  return result;
}

function processNonLatexText(text: string, pattern: RegExp): string {
  if (!text) return text;
  
  // 创建新的正则表达式实例避免状态污染
  const newPattern = new RegExp(pattern.source, pattern.flags);
  
  return text.replace(newPattern, (match) => `$${match}$`);
}

// 合并日期，['2025-06-06', '2025-06-09', '2025-06-10']
// 如果是单独的日期，则返回new Date(2022, 5, 8)，如果是连续的日期则返回：{ from: new Date(2022, 5, 15), to: new Date(2022, 5, 20) }
// [
//   new Date(2022, 5, 8),
//   new Date(2022, 5, 9),
//   new Date(2022, 5, 10),
//   { from: new Date(2022, 5, 15), to: new Date(2022, 5, 20) }
// ]
export function mergeConsecutiveDates(dates: string[]): (Date | { from: Date; to: Date })[] {
  const parsedDates = dates
    .map(dateStr => {
      // Parse as local timezone to avoid UTC offset issues
      // "2025-11-17" -> local date 2025-11-17 00:00:00
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    })
    .sort((a, b) => a.getTime() - b.getTime());

  const results: (Date | { from: Date; to: Date })[] = [];

  let rangeStart: Date | null = null;
  let prevDate: Date | null = null;

  for (let i = 0; i <= parsedDates.length; i++) {
    const current = parsedDates[i];
    const nextExpected = prevDate && new Date(prevDate.getTime() + 86400000); // +1天

    if (prevDate && current && nextExpected && nextExpected.toDateString() === current.toDateString()) {
      // 是连续的，继续范围
      if (!rangeStart) rangeStart = prevDate;
    } else {
      if (rangeStart && prevDate) {
        // 收尾一个范围
        results.push({ from: rangeStart, to: prevDate });
        rangeStart = null;
      } else if (prevDate) {
        // 是单独日期
        results.push(prevDate);
      }
    }

    prevDate = current || null;
  }

  return results;
}

// 创建一个挂载点
export function createOverlayRoot(id: string) {
  if (typeof window === 'undefined') return null;
  if (document.getElementById(id)) return document.getElementById(id);

  const root = document.createElement("div");
  root.id = id;
  document.body.appendChild(root);
  return root;
}

export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  let lastArgs: Parameters<T> | null = null;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      
      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          fn(...lastArgs);
          lastArgs = null;
          inThrottle = true;
          setTimeout(() => {
            inThrottle = false;
          }, limit);
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };
}

// 给予一个数组重新排列，使用Fisher-Yates洗牌算法
export function shuffleArray<T>(array: T[]): T[] {
  // 确保输入是数组
  if (!Array.isArray(array)) {
    console.warn('shuffleArray: input is not an array:', array);
    return [];
  }
  
  // 创建数组副本，避免修改原数组
  const shuffled = [...array];
  
  // Fisher-Yates洗牌算法
  for (let i = shuffled.length - 1; i > 0; i--) {
    // 生成0到i之间的随机整数
    const j = Math.floor(Math.random() * (i + 1));
    // 交换元素
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
}


// 2025-07-10T09:49:39.875198+00:00
// 与当前时间做对比，返回1 min ago, 1 hour ago, 1 day ago, 1 month ago, 1 year ago
export function formatTime(time: string) {
  const now = new Date();
  const date = new Date(time);
  const diff = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diff / (1000 * 60));
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 min ago' : `${diffMinutes} mins ago`;
  }
  const diffHours = Math.floor(diff / (1000 * 60 * 60));
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (diffDays < 30) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }
  const diffMonths = Math.floor(diff / (1000 * 60 * 60 * 24 * 30));
  if (diffMonths < 12) {
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  }
  const diffYears = Math.floor(diff / (1000 * 60 * 60 * 24 * 30 * 12));
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

/**
 * 模糊搜索学校名称
 * @param schools 学校数组
 * @param searchInput 搜索输入
 * @param searchFields 搜索字段数组，默认为 ['school_name', 'city']
 * @param limit 返回结果数量限制，默认为 10
 * @returns 过滤和排序后的学校数组
 */
export function fuzzySearchSchools<T extends Record<string, any>>(
  schools: T[],
  searchInput: string,
  searchFields: string[] = ['school_name', 'city'],
  limit: number = 10
): T[] {
  if (!searchInput || searchInput.length < 2) {
    return [];
  }

  const searchTerm = searchInput.toLowerCase().trim();
  const searchWords = searchTerm.split(/\s+/);

  const filtered = schools
    .filter(school => {
      // 检查所有搜索词是否都能在任意搜索字段中找到
      return searchWords.every(word => 
        searchFields.some(field => {
          const fieldValue = school[field]?.toLowerCase() || '';
          return fieldValue.includes(word);
        })
      );
    })
    .sort((a, b) => {
      // 按相关性排序：完全匹配 > 开头匹配 > 包含匹配
      const primaryField = searchFields[0]; // 使用第一个字段作为主要排序字段
      const aValue = a[primaryField]?.toLowerCase() || '';
      const bValue = b[primaryField]?.toLowerCase() || '';
      
      // 完全匹配优先级最高
      if (aValue === searchTerm) return -1;
      if (bValue === searchTerm) return 1;
      
      // 开头匹配优先级次之
      if (aValue.startsWith(searchTerm) && !bValue.startsWith(searchTerm)) return -1;
      if (bValue.startsWith(searchTerm) && !aValue.startsWith(searchTerm)) return 1;
      
      // 相同优先级下按字母顺序排序
      return aValue.localeCompare(bValue);
    })
    .slice(0, limit);

  return filtered;
}

export function capitalizeFirstLetter(str: string): string {
  if (!str || str.length === 0) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Get date in local timezone as YYYY-MM-DD string
 * This avoids timezone offset issues when using toISOString()
 * @param date - Optional Date object to convert. If not provided, uses today's date
 * @returns Date string in format "YYYY-MM-DD" (e.g., "2025-11-17")
 *
 * @example
 * getLocalDateString() // "2025-11-17" (today)
 * getLocalDateString(new Date()) // "2025-11-17"
 * const yesterday = new Date();
 * yesterday.setDate(yesterday.getDate() - 1);
 * getLocalDateString(yesterday) // "2025-11-16"
 */
export function getLocalDateString(date?: Date): string {
  const targetDate = date || new Date();
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, '0');
  const day = String(targetDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getLocalMonthString(date?: Date): string {
  if (isLearnTest) {
    const overrideMonth = resolveLearnTestMonthOverride();
    if (overrideMonth) {
      return overrideMonth;
    }
  }

  const targetDate = date || new Date();
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Get monthly event end date in local timezone.
 * End date is the first day of next month at 00:00:00.
 * If month is omitted, it resolves from getLocalMonthString().
 */
export function getMonthlyEventEndDate(month?: string): Date {
  const targetMonth = month ?? getLocalMonthString();
  const match = /^(\d{4})-(\d{2})$/.exec(targetMonth);

  if (!match) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  return new Date(year, monthIndex + 1, 1, 0, 0, 0);
}
